import { useState } from "react";
import { Mail } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

export default function Contact() {
  const [contact, setContact] = useState({ name: "", email: "", subject: "", message: "" });
  const [contactBusy, setContactBusy] = useState(false);

  async function submitContact(e) {
    e.preventDefault();
    if (!contact.name || !contact.email || !contact.message) {
      toast.error("Please fill name, email and message");
      return;
    }
    setContactBusy(true);
    try {
      await api.post("/contact", contact);
      toast.success("Thank you. We'll reply within 3 working days.");
      setContact({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send");
    } finally {
      setContactBusy(false);
    }
  }

  return (
    <section id="contact" data-testid="contact-section" className="relative py-16 md:py-28 bg-parchment-light">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 lg:gap-12 items-start">
        <div className="text-center md:text-left">
          <span className="section-chip">Contact</span>
          <h2 className="mt-3 font-display font-extrabold text-navy text-3xl sm:text-4xl md:text-5xl leading-[1.1] tracking-tight">
            Write to us. We reply.
          </h2>
          <p className="mt-4 text-indigo/80 font-display">
            Whether you would like to volunteer, donate, host us at your school, or simply learn more —
            this is where a real conversation begins.
          </p>

          <div className="mt-8 grid gap-6">
            <div className="border-l-4 border-teal pl-4 text-left">
              <div className="text-xs uppercase tracking-widest text-teal font-display font-semibold">Bengaluru HQ</div>
              <div className="mt-1 font-display text-navy/80 text-sm leading-relaxed">
                #44, Kamath House,<br/>
                Classic Orchards Layout, Kalena Agrahara,<br/>
                Kothnur, Bengaluru &mdash; 560076
              </div>
            </div>
          </div>

          <div className="mt-6 space-y-2 font-display text-sm text-navy/80">
            <div className="flex items-center gap-2 justify-center md:justify-start">
              <Mail className="w-4 h-4 text-teal" />
              <a href="mailto:connect@aksharadaana.org" className="hover:text-teal">connect@aksharadaana.org</a>
            </div>
          </div>
        </div>

        <form onSubmit={submitContact} data-testid="contact-form" className="manuscript-card">
          <h3 className="font-display font-extrabold text-navy text-2xl">Send a message</h3>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input data-testid="contact-name" required placeholder="Name" value={contact.name}
              onChange={(e) => setContact({ ...contact, name: e.target.value })}
              className="min-w-0 bg-parchment-light border border-indigo/25 focus:border-vermilion px-3 py-2.5 font-display text-sm text-indigo focus:outline-none" />
            <input data-testid="contact-email" type="email" required placeholder="Email" value={contact.email}
              onChange={(e) => setContact({ ...contact, email: e.target.value })}
              className="min-w-0 bg-parchment-light border border-indigo/25 focus:border-vermilion px-3 py-2.5 font-display text-sm text-indigo focus:outline-none" />
            <input data-testid="contact-subject" placeholder="Subject" value={contact.subject}
              onChange={(e) => setContact({ ...contact, subject: e.target.value })}
              className="min-w-0 sm:col-span-2 bg-parchment-light border border-indigo/25 focus:border-vermilion px-3 py-2.5 font-display text-sm text-indigo focus:outline-none" />
            <textarea data-testid="contact-message" required rows={4} placeholder="Your message" value={contact.message}
              onChange={(e) => setContact({ ...contact, message: e.target.value })}
              className="min-w-0 sm:col-span-2 bg-parchment-light border border-indigo/25 focus:border-vermilion px-3 py-2.5 font-display text-sm text-indigo focus:outline-none" />
          </div>
          <button data-testid="contact-submit" type="submit" disabled={contactBusy} className="mt-5 btn-vermilion disabled:opacity-60">
            {contactBusy ? "Sending…" : "Send message"}
          </button>
        </form>
      </div>
    </section>
  );
}
